import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PricingContactSection = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    planInterest: '',
    budget: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const translations = {
    en: {
      title: "Ready to Get Started?",
      subtitle: "Get a personalized quote and speak with our pricing experts",
      form: {
        name: "Full Name",
        email: "Business Email",
        phone: "Phone Number",
        company: "Company Name",
        planInterest: "Plan of Interest",
        budget: "Monthly Budget Range",
        timeline: "Implementation Timeline",
        message: "Specific Requirements",
        submit: "Get Personalized Quote"
      },
      placeholders: {
        name: "Enter your full name",
        email: "your.email@company.com",
        phone: "+91 98765 43210",
        company: "Your company name",
        planInterest: "Select plan",
        budget: "Select budget range",
        timeline: "Select timeline",
        message: "Tell us about your specific pricing needs..."
      },
      options: {
        plans: [
          "Starter Plan",
          "Professional Plan",
          "Enterprise Plan",
          "Custom Solution"
        ],
        budget: [
          "₹10,000 - ₹25,000",
          "₹25,000 - ₹50,000",
          "₹50,000 - ₹1,00,000",
          "₹1,00,000+"
        ],
        timeline: [
          "Immediate (within 1 month)",
          "Short term (1-3 months)",
          "Medium term (3-6 months)",
          "Long term (6+ months)"
        ]
      },
      benefits: {
        title: "Why Choose Our Pricing?",
        items: [
          {
            icon: "Shield",
            title: "Transparent Pricing",
            description: "No hidden costs or surprise fees"
          },
          {
            icon: "Percent",
            title: "Best Value",
            description: "Competitive rates with premium features"
          },
          {
            icon: "Clock",
            title: "Flexible Payment",
            description: "Monthly, quarterly, or annual billing"
          },
          {
            icon: "Award",
            title: "Money-Back Guarantee",
            description: "30-day satisfaction guarantee"
          }
        ]
      },
      contact: {
        title: "Or Contact Us Directly",
        phone: "+91 98765 43210",
        email: "pricing@alumerp.com",
        hours: "Mon-Sat: 9:00 AM - 7:00 PM IST"
      },
      success: "Thank you! Our pricing team will contact you within 24 hours.",
      error: "Something went wrong. Please try again."
    },
    hi: {
      title: "शुरू करने के लिए तैयार?",
      subtitle: "व्यक्तिगत कोट प्राप्त करें और हमारे मूल्य निर्धारण विशेषज्ञों से बात करें",
      form: {
        name: "पूरा नाम",
        email: "व्यावसायिक ईमेल",
        phone: "फोन नंबर",
        company: "कंपनी का नाम",
        planInterest: "रुचि की योजना",
        budget: "मासिक बजट रेंज",
        timeline: "कार्यान्वयन समयसीमा",
        message: "विशिष्ट आवश्यकताएं",
        submit: "व्यक्तिगत कोट प्राप्त करें"
      },
      placeholders: {
        name: "अपना पूरा नाम दर्ज करें",
        email: "your.email@company.com",
        phone: "+91 98765 43210",
        company: "आपकी कंपनी का नाम",
        planInterest: "योजना चुनें",
        budget: "बजट रेंज चुनें",
        timeline: "समयसीमा चुनें",
        message: "अपनी विशिष्ट मूल्य निर्धारण आवश्यकताओं के बारे में बताएं..."
      },
      options: {
        plans: [
          "स्टार्टर योजना",
          "प्रोफेशनल योजना",
          "एंटरप्राइज़ योजना",
          "कस्टम समाधान"
        ],
        budget: [
          "₹10,000 - ₹25,000",
          "₹25,000 - ₹50,000",
          "₹50,000 - ₹1,00,000",
          "₹1,00,000+"
        ],
        timeline: [
          "तत्काल (1 महीने के भीतर)",
          "अल्पकालिक (1-3 महीने)",
          "मध्यम अवधि (3-6 महीने)",
          "दीर्घकालिक (6+ महीने)"
        ]
      },
      benefits: {
        title: "हमारे मूल्य निर्धारण को क्यों चुनें?",
        items: [
          {
            icon: "Shield",
            title: "पारदर्शी मूल्य निर्धारण",
            description: "कोई छुपी हुई लागत या आश्चर्य शुल्क नहीं"
          },
          {
            icon: "Percent",
            title: "सबसे अच्छा मूल्य",
            description: "प्रीमियम सुविधाओं के साथ प्रतिस्पर्धी दरें"
          },
          {
            icon: "Clock",
            title: "लचीला भुगतान",
            description: "मासिक, त्रैमासिक, या वार्षिक बिलिंग"
          },
          {
            icon: "Award",
            title: "पैसे वापसी की गारंटी",
            description: "30-दिन संतुष्टि गारंटी"
          }
        ]
      },
      contact: {
        title: "या हमसे सीधे संपर्क करें",
        phone: "+91 98765 43210",
        email: "pricing@alumerp.com",
        hours: "सोम-शनि: सुबह 9:00 - शाम 7:00 IST"
      },
      success: "धन्यवाद! हमारी मूल्य निर्धारण टीम 24 घंटे के भीतर आपसे संपर्क करेगी।",
      error: "कुछ गलत हुआ। कृपया पुनः प्रयास करें।"
    }
  };

  const t = translations[currentLanguage];

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      
      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          planInterest: '',
          budget: '',
          timeline: '',
          message: ''
        });
        setSubmitStatus(null);
      }, 3000);
    }, 2000);
  };

  return (
    <section id="pricing-contact" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            {t.title}
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-border">
            {submitStatus === 'success' ? (
              <div className="text-center py-12">
                <Icon name="CheckCircle" size={64} color="var(--color-success)" className="mx-auto mb-6" />
                <h3 className="text-xl font-bold text-success mb-4">
                  {currentLanguage === 'en' ? 'Thank You!' : 'धन्यवाद!'}
                </h3>
                <p className="text-text-secondary">{t.success}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name and Email */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t.form.name} *
                    </label>
                    <Input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder={t.placeholders.name}
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t.form.email} *
                    </label>
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder={t.placeholders.email}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Phone and Company */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t.form.phone} *
                    </label>
                    <Input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder={t.placeholders.phone}
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t.form.company} *
                    </label>
                    <Input
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      placeholder={t.placeholders.company}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Plan Interest and Budget */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t.form.planInterest}
                    </label>
                    <select
                      value={formData.planInterest}
                      onChange={(e) => handleInputChange('planInterest', e.target.value)}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">{t.placeholders.planInterest}</option>
                      {t.options.plans.map((plan, index) => (
                        <option key={index} value={plan}>{plan}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-text-secondary mb-2">
                      {t.form.budget}
                    </label>
                    <select
                      value={formData.budget}
                      onChange={(e) => handleInputChange('budget', e.target.value)}
                      className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">{t.placeholders.budget}</option>
                      {t.options.budget.map((range, index) => (
                        <option key={index} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Timeline */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t.form.timeline}
                  </label>
                  <select
                    value={formData.timeline}
                    onChange={(e) => handleInputChange('timeline', e.target.value)}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">{t.placeholders.timeline}</option>
                    {t.options.timeline.map((option, index) => (
                      <option key={index} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    {t.form.message}
                  </label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder={t.placeholders.message}
                    rows={4}
                    className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={isSubmitting}
                  className="w-full bg-gradient-accent hover:bg-accent-600 shadow-cta"
                  iconName="MessageCircle"
                  iconPosition="left"
                >
                  {t.form.submit}
                </Button>
              </form>
            )}
          </div>

          {/* Benefits and Contact Info */}
          <div className="space-y-8">
            {/* Benefits */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-border">
              <h3 className="text-xl font-bold text-primary mb-6">
                {t.benefits.title}
              </h3>
              
              <div className="space-y-6">
                {t.benefits.items.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
                      <Icon name={benefit.icon} size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h4 className="font-bold text-primary mb-2">
                        {benefit.title}
                      </h4>
                      <p className="text-sm text-text-secondary">
                        {benefit.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Direct Contact */}
            <div className="bg-gradient-accent rounded-2xl p-8 text-white">
              <h3 className="text-xl font-bold mb-6">
                {t.contact.title}
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-white bg-opacity-20 rounded-lg">
                    <Icon name="Phone" size={20} color="white" />
                  </div>
                  <div>
                    <p className="font-medium">{t.contact.phone}</p>
                    <p className="text-sm opacity-80">{t.contact.hours}</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-white bg-opacity-20 rounded-lg">
                    <Icon name="Mail" size={20} color="white" />
                  </div>
                  <div>
                    <p className="font-medium">{t.contact.email}</p>
                    <p className="text-sm opacity-80">
                      {currentLanguage === 'en' ? 'Pricing & Sales' : 'मूल्य निर्धारण और बिक्री'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-white border-opacity-20">
                <Button
                  variant="ghost"
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white border-white"
                  iconName="MessageCircle"
                  iconPosition="left"
                >
                  {currentLanguage === 'en' ? 'WhatsApp Us' : 'हमें WhatsApp करें'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingContactSection;